import React, { useMemo } from 'react';
import SimpleModal from 'ui/Modals/SimpleModal';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { ColDef } from 'ag-grid-community';
import HrDataGrid from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import ChipCellRenderer from 'ui/AgGridReact/CellRenderers/ChipCellRenderer';
import { useTranslation } from 'react-i18next';
import { getColDefs } from './colDefinitions';
import BottomPinnedRowRenderer from 'routes/HumanResourcesModule/components/HrDataGrid/BottomPinnedRowRenderer';
import { SmicDataBase } from '../helpers/hooks/smic';
import {
    INDICIAIRE_STATUSES,
    NON_INDICIAIRE_STATUSES,
} from '../helpers/dataHelpers';
import { SimulationTypeEnum } from 'routes/HumanResourcesModule/Simulation/helpers/constants';

type Props = {
    hrSimulation: HrSimulation;
    smicHypotheses: SmicDataBase[];
    isSmicHypothesesLoading: boolean;
    showModal: boolean;
    indiciaire: boolean;
    setShowModal: (arg: boolean) => void;
};

const DEFAULT_COL_DEF = {
    flex: 1,
    minWidth: 80,
    sortable: true,
    resizable: true,
    suppressMovable: true,
    suppressFiltersToolPanel: true,
};

export default function SmicHypothesesModal({
    hrSimulation,
    smicHypotheses,
    isSmicHypothesesLoading,
    showModal,
    indiciaire,
    setShowModal,
}: Props) {
    const { t } = useTranslation();
    const isBudgetProposal =
        hrSimulation.type === SimulationTypeEnum.BUDGET_PROPOSAL;

    const columnDefs: ColDef[] = useMemo(
        () =>
            getColDefs(
                t,
                Number(hrSimulation.startDate.slice(0, 4)),
                isBudgetProposal,
            ),
        [hrSimulation],
    );

    const indiciaireValue = indiciaire
        ? INDICIAIRE_STATUSES
        : NON_INDICIAIRE_STATUSES;

    const data = smicHypotheses.filter((o) =>
        indiciaireValue.includes(o.mantyStatus),
    );
    return (
        <SimpleModal
            open={showModal}
            onClose={() => setShowModal(false)}
            maxWidth="xl"
        >
            <HrDataGrid
                title={t('humanResources.simulation.smic.modalTitle', {
                    indiciaire: indiciaire ? 'indirecte' : 'directe',
                })}
                rowData={data}
                columnDefs={columnDefs}
                isLoading={isSmicHypothesesLoading}
                exportFileName={`hypotheses_smic_${hrSimulation.title}`}
                exportSheetName={t(
                    `humanResources.simulation.smic.exportSheetName${indiciaire ? 'Indirect' : 'Direct'}`,
                )}
                hasFullScreenMode={false}
                showTotal={false}
                gridProps={{
                    components: {
                        bottomPinnedRowRenderer: BottomPinnedRowRenderer,
                        textFloatingFilter: TextFloatingFilter,
                        numberFloatingFilter: NumberFloatingFilter,
                        chipCellRenderer: ChipCellRenderer,
                    },
                    rowGroupPanelShow: 'never',
                    defaultColDef: {
                        ...DEFAULT_COL_DEF,
                    },
                    pagination: false,
                    alwaysShowVerticalScroll: true,
                    suppressScrollOnNewData: false,
                }}
                sx={{
                    maxHeight: '600px',
                    '& .ag-body-viewport': {
                        overflowY: 'auto',
                        maxHeight: '500px',
                    },
                    '& .ag-body-vertical-scroll': {
                        maxHeight: '500px',
                    },
                }}
            />
        </SimpleModal>
    );
}
